import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

function Header() {
  const location = useLocation();
  const pathname = location.pathname;
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const dropdownRef = useRef(null);
  const itemRefs = useRef([]);
  const navigate = useNavigate();

  const toggleMenu = () => setIsOpen(!isOpen);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navItems = [
    {
      name: "Product",
      href: "/",
      hasDropdown: true,
      isClass: true,
      dropdownItems: [
        {
          name: "Landlord",
          logo: "/assets/header/landlord.svg",
          href: "/product/landlord",
        },
        {
          name: "Tenant",
          logo: "/assets/header/tenant.svg",
          href: "/product/tennant",
        },
      ],
    },
    {
      name: "Features",
      href: "/features",
      hasDropdown: true,
      isClass: false,
      dropdownItems: [
        {
          name: "Tenant Management",
          logo: "/assets/header/tenant-managment.svg",
          href: "/features/tenant-management",
        },
        {
          name: "Maintenance Requests",
          logo: "/assets/header/maintainance.svg",
          href: "/features/maintenance-requests",
        },
        {
          name: "Property and Unit Creation",
          logo: "/assets/header/property-unit.svg",
          href: "/features/property-unit-creation",
        },
        {
          name: "Lease Contracts",
          logo: "/assets/header/lease-maintainnance.svg",
          href: "/features/lease-contracts",
        },
        {
          name: "Maintenance Request Summary",
          logo: "/assets/header/lease-maintainnance.svg",
          href: "/features/maintenance-request-summary",
        },
        {
          name: "Property Summary",
          logo: "/assets/header/property-summary.svg",
          href: "/features/property-summary",
        },
        {
          name: "Broadcast",
          logo: "/assets/header/broadcast.svg",
          href: "/features/broadcast",
        },
        {
          name: "Lease Reminders",
          logo: "/assets/header/lease-reminder.svg",
          href: "/features/lease-reminders",
        },
        // {
        //   name: "Help And Tutorials",
        //   logo: "/assets/header/help.svg",
        //   href: "/features/help-tutorial",
        // }
        // {
        //   name: "Live Chat Support",
        //   logo: "/assets/header/chat.svg",
        //   href: "/features/live-chat-support",
        // },
      ],
    },
    { name: "Why Flex", href: "/why-flex" },
    { name: "Pricing", href: "/package" },
    { name: "Contact Us", href: "/contact-us" },
  ];

  // Set active index based on current path
  useEffect(() => {
    const index = navItems.findIndex(
      (item) =>
        pathname === item.href ||
        (item.hasDropdown &&
          item.dropdownItems?.some((subItem) => pathname === subItem.href))
    );
    if (index !== -1) {
      setActiveIndex(index);
    }
  }, [pathname]);

  // Get dimensions for the active indicator
  const getActiveStyles = () => {
    if (hoveredIndex !== null) {
      const currentItem = itemRefs.current[hoveredIndex];
      if (currentItem) {
        return {
          width: currentItem.offsetWidth,
          left: currentItem.offsetLeft,
        };
      }
    }

    const currentItem = itemRefs.current[activeIndex];
    if (currentItem) {
      return {
        width: currentItem.offsetWidth,
        left: currentItem.offsetLeft,
      };
    }

    return {
      width: 0,
      left: 0,
    };
  };

  const activeStyles = getActiveStyles();

  return (
    <header
      className={`${
        pathname === "/" ? "absolute top-0 left-0 w-full z-50" : "relative"
      } border-b border-blue-100 bg-white shadow-sm py-4`}
    >
      <div className="w-[90%] mx-auto px-4">
        <div className="flex pt-2 items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-shrink-0"
          >
            <Link to="/" className="flex items-center">
              <img src="/assets/logo.png" alt="Logo" className="h-20 w-40" />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hidden lg:block"
            ref={dropdownRef}
          >
            <ul className="relative flex items-center space-x-4">
              {/* Active background indicator - FIXED with more visible background */}
              <motion.div
                className="absolute bottom-0 top-0 -z-10 rounded-md bg-blue-500"
                initial={false}
                animate={{
                  width: activeStyles.width,
                  left: activeStyles.left,
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 28,
                  mass: 1.3,
                }}
                style={{ opacity: 0.15 }} // Using inline style for opacity to ensure it works
              />

              {navItems.map((item, index) => {
                const isActive =
                  index === activeIndex ||
                  (item.hasDropdown &&
                    item.dropdownItems?.some(
                      (subItem) => pathname === subItem.href
                    ));

                return (
                  <motion.li
                    key={item.name}
                    ref={(el) => (itemRefs.current[index] = el)}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      ease: "easeOut",
                      delay: index * 0.08,
                    }}
                    className="relative flex justify-center items-center px-1"
                    onMouseEnter={() => {
                      setHoveredIndex(index);
                      if (item.hasDropdown) setActiveDropdown(item.name);
                    }}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                      if (item.hasDropdown) setActiveDropdown(null);
                    }}
                  >
                    {/* 🔵 Animated Active Background - Small to Full Width */}
                    {isActive && (
                      <motion.div
                        layoutId="activeNavBg"
                        initial={{ scaleX: 0.2, opacity: 0 }}
                        animate={{ scaleX: 1, opacity: 1 }}
                        exit={{ scaleX: 0.2, opacity: 0 }}
                        transition={{
                          type: "spring",
                          stiffness: 220,
                          damping: 26,
                          mass: 0.6,
                          duration: 0.5,
                        }}
                        className="absolute inset-0 origin-left text-center rounded-md bg-blue-100 shadow-md z-0"
                      />
                    )}

                    {/* 🔗 Text */}
                    {item.hasDropdown ? (
                      <p
                        className={`relative z-10 md:px-2 xl:px-4 py-2 text-center w-full text-xs xl:text-base font-popinsSemiBold transition-colors duration-300 cursor-pointer ${
                          isActive
                            ? "text-blue-800"
                            : "text-gray-600 hover:text-blue-700"
                        }`}
                      >
                        {item.name}
                      </p>
                    ) : (
                      <Link
                        to={item.href}
                        className={`relative z-10 md:px-2 xl:px-4 py-2 text-center w-full text-xs xl:text-base font-popinsSemiBold transition-colors duration-300 ${
                          isActive
                            ? "text-blue-800"
                            : "text-gray-600 hover:text-blue-700"
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}

                    {/* ⬇️ Dropdown if exists */}
                    {item.hasDropdown && activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.25 }}
                        className={`absolute left-0 top-7 z-10 mt-1 ${
                          item.isClass ? "w-60" : "w-[380px]"
                        } p-4 origin-top-left rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 border-b-[3px] border-btnColor`}
                      >
                        {item.dropdownItems?.map((dropdownItem) => (
                          <div className="flex hover:bg-gray-100 hover:px-2 mt-2">
                            <img src={dropdownItem.logo} alt="" />
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              className="block px-4 py-2 text-base font-popinsSemiBold text-gray-700"
                            >
                              {dropdownItem.name}
                            </Link>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </motion.li>
                );
              })}
            </ul>
          </motion.nav>

          {/* Right side button */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="hidden lg:block"
          >
            {/* Request Demo Button (without dropdown) */}
            <a
              href="https://landloard.dev.theflexmanager.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center rounded-md font-popinsMedium bg-themeColor px-2 xl:px-4 py-2 text-xs xl:text-sm font-medium text-white shadow-md transition-all hover:bg-themeColor hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Become a Partner
            </a>
          </motion.div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="lg:hidden"
        >
          <div className="space-y-1 px-2 pb-3 pt-2">
            {navItems.map((item, index) => {
              const isActive =
                index === activeIndex ||
                (item.hasDropdown &&
                  item.dropdownItems?.some(
                    (subItem) => pathname === subItem.href
                  ));

              return (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <p
                      className={`block rounded-md px-3 py-2 text-base font-popinsMedium hover:text-blue-600 ${
                        isActive
                          ? "bg-blue-500 bg-opacity-15 text-blue-700"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        if (item.hasDropdown) {
                          setActiveDropdown(
                            activeDropdown === item.name ? null : item.name
                          );
                        } else {
                          setIsOpen(false);
                        }
                      }}
                    >
                      {item.name}
                    </p>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block rounded-md px-3 py-2 text-base font-popinsMedium hover:text-blue-600 ${
                        isActive
                          ? "bg-blue-500 bg-opacity-15 text-blue-700"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        if (item.hasDropdown) {
                          setActiveDropdown(
                            activeDropdown === item.name ? null : item.name
                          );
                        } else {
                          setIsOpen(false);
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                  )}

                  {/* Mobile dropdown for Features */}
                  {item.hasDropdown && activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="ml-4 space-y-1 rounded-md bg-gray-50 p-2"
                    >
                      {item.dropdownItems?.map((dropdownItem) => {
                        return (
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setIsOpen(false);
                              navigate(dropdownItem.href);
                            }}
                            className="flex cursor-pointer hover:bg-gray-100 hover:px-2 mt-2"
                          >
                            <img src={dropdownItem.logo} alt="" />
                            <span className="block px-3 py-2 text-sm font-popinsMedium">
                              {dropdownItem.name}
                            </span>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </div>
              );
            })}

            <div className="mt-4 px-3">
              <a
                href="https://landloard.dev.theflexmanager.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center font-popinsMedium rounded-md bg-themeColor px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-themeColor/80"
                onClick={() => setIsOpen(false)}
              >
                Become a Partner
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </header>
  );
}

export default Header;
